import {
  DatePicker,
  Form,
  Input,
  InputProps,
  Modal,
  Select,
  Space,
} from 'antd';
import { Rule } from 'antd/es/form';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import {
  Dispatch,
  forwardRef,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { EditIcon, XIcon } from 'assets/svg';
import { ButtonAction, Switch } from 'components';
import TableComponent from 'components/Table/TableComponent';
import useQueryParam from 'hooks/useQueryPrams';
import { ISaleDeal } from 'interfaces/SaleDeal';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { convertDateToBE } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';
import { ISaleDealData } from './SaleDealPage';

interface IProps {
  loading: boolean;
  data: ISaleDealData;
  selectedIndex: number;
  onSave: (values: ISaleDeal) => void;
  setData: Dispatch<SetStateAction<ISaleDealData>>;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
}

interface IRenderTable {
  index: number;
  rules?: Rule[];
  inputProps: InputProps;
  component?: ReactNode;
}

const statusOptions = [
  { label: 'Hoạt động', value: true },
  { label: 'Không hoạt động', value: false },
];

const newItem = {
  status: true,
  sdFromDate: dayjs(new Date()),
} as ISaleDeal;

const { confirm } = Modal,
  { SaveTwoTone, CloseCircleTwoTone } = icons;

const TableSaleDeal = forwardRef((props: IProps, ref: any) => {
  const [form] = useForm(),
    navigate = useNavigate();

  const { data, loading, selectedIndex, onSave, setSelectedIndex, setData } =
    props;

  const [isAddRow, setIsAddRow] = useState(false);

  const isDisabled = useMemo(() => {
    return selectedIndex !== -1;
  }, [selectedIndex]);

  const isCreated = useMemo(() => {
    return selectedIndex === 0 && !data.items[0]?.id;
  }, [selectedIndex, data]);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  useImperativeHandle(ref, () => ({
    handleAddRow,
  }));

  const renderTable = useCallback(
    (props: IRenderTable) => {
      const { index, rules, component, inputProps } = props;

      const { type, name, value } = inputProps,
        customeRules = rules !== undefined ? rules : ([] as Rule[]);

      const renderValue = () => {
        if (type === 'status') return value ? 'Hoạt động' : 'Không hoạt động';

        if (!value) return '-';

        if (type === 'date')
          return dayjs(value?.toString()).format('DD/MM/YYYY');

        return value?.toString();
      };

      return index === selectedIndex ? (
        <Form.Item
          name={name}
          className="m-0"
          rules={customeRules}
          initialValue={value}
        >
          {component ? component : <Input {...inputProps} />}
        </Form.Item>
      ) : (
        renderValue()
      );
    },
    [selectedIndex],
  );

  const handleAddRow = () => {
    form.resetFields();

    if (page === 1) {
      setData((prev) => ({
        ...prev,
        items: [newItem, ...prev.items],
      }));
      setSelectedIndex(0);

      return;
    }

    setIsAddRow(true);
    navigate(`?page=1&page_size=${page_size}`);
  };

  const handleClickEditRow = (index: number) => {
    form.resetFields();

    const dataIndex = data.items[index];
    const fieldsValue = {
      ...dataIndex,
      sdFromDate: dayjs(dataIndex.sdFromDate),
      sdToDate: dayjs(dataIndex.sdToDate),
    };

    setSelectedIndex(index);
    form.setFieldsValue(fieldsValue);
  };

  const handleChangeSwitch = (record: any) => {
    console.log('switch', record);
  };

  const handleClickDeleteRow = (id: string) => {
    console.log('delete', id);
  };

  const checkFormValidate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSave = async (record: ISaleDeal) => {
    if (!(await checkFormValidate())) {
      form.validateFields();
      return;
    }

    const fieldsValue = form.getFieldsValue();
    const newRecord = {
      ...record,
      ...fieldsValue,
      sdFromDate: convertDateToBE(fieldsValue.sdFromDate),
      sdToDate: convertDateToBE(fieldsValue.sdToDate),
    };

    onSave(newRecord);
  };

  const handleCancel = () => {
    form.resetFields();

    if (selectedIndex === 0 && !data.items[0]?.id)
      setData((prev) => ({
        ...prev,
        items: prev.items.slice(1),
      }));
    setSelectedIndex(-1);
  };

  const columns: ColumnsTypeCustom = useMemo(() => {
    return [
      {
        width: 50,
        title: 'STT',
        className: 'align-top',
        render: (_, __, index) =>
          data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
          index +
          1,
      },
      {
        width: 120,
        title: 'Mã CTKM',
        className: 'align-top',
        dataIndex: 'saleDeal',
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'saleDeal',
            },
            rules: [{ required: true, message: 'Nhập mã CTKM' }],
          });
        },
      },
      {
        width: 320,
        className: 'align-top',
        title: 'Tên chương trình',
        dataIndex: 'saleDealName',
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'saleDealName',
            },
            rules: [{ required: true, message: 'Nhập tên chương trình' }],
          });
        },
      },
      {
        width: 100,
        title: 'Nội dung',
        dataIndex: 'content',
        className: 'align-top',
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'content',
            },
            rules: [{ required: true, message: 'Nhập nội dung' }],
          });
        },
      },
      {
        width: 100,
        title: 'Loại CK',
        className: 'align-top',
        dataIndex: ['tconditionType', 'id'],
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'conditionTypeId',
            },
            rules: [{ required: true, message: '' }],
          });
        },
      },
      {
        width: 170,
        title: 'Tên loại CK',
        className: 'align-top',
        dataIndex: ['tconditionType', 'name'],
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'tconditionTypeName',
            },
            rules: [{ required: true, message: '' }],
          });
        },
      },
      {
        width: 170,
        className: 'align-top',
        title: 'Giá trị/ phần trăm CK',
        dataIndex: ['tconditionType', 'value'],
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'tconditionTypeValue',
            },
            rules: [{ required: true, message: '' }],
          });
        },
      },
      {
        width: 100,
        title: 'Tiền tệ',
        dataIndex: 'unitMoney',
        className: 'align-top',
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              name: 'unitMoney',
            },
            rules: [{ required: true, message: '' }],
          });
        },
      },
      {
        width: 150,
        title: 'Trạng thái',
        dataIndex: 'status',
        className: 'align-top',
        render: (value, _, index) => {
          return renderTable({
            index: index,
            inputProps: {
              value: value,
              type: 'status',
              name: 'status',
            },
            component: <Select disabled={isCreated} options={statusOptions} />,
          });
        },
      },
      {
        width: 150,
        title: 'Ngày bắt đầu',
        className: 'align-top',
        dataIndex: 'sdFromDate',
        render: (value, _, index) => {
          const disabledDate = (currentDate: dayjs.Dayjs) => {
            const today = new Date(),
              yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);

            return dayjs(currentDate).toISOString() < yesterday.toISOString();
          };

          return renderTable({
            index: index,
            inputProps: {
              type: 'date',
              value: value,
              name: 'sdFromDate',
            },
            component: (
              <DatePicker
                picker="date"
                disabledDate={disabledDate}
                placeholder="Chọn ngày bắt đầu"
                defaultValue={dayjs(new Date())}
                format={{ format: 'DD/MM/YYYY' }}
                onChange={() => form.setFieldValue('sdToDate', undefined)}
              />
            ),
            rules: [{ required: true, message: 'Chọn ngày bắt đầu' }],
          });
        },
      },
      {
        width: 150,
        title: 'Ngày kết thúc',
        className: 'align-top',
        dataIndex: 'sdToDate',
        render: (value, _, index) => {
          const disabledDate = (currentDate: dayjs.Dayjs) => {
            const dateActive = form.getFieldValue('sdFromDate'),
              formatedDateActive =
                dateActive === null || dateActive === ''
                  ? new Date()
                  : dateActive;

            return dayjs(currentDate).isBefore(
              dayjs(formatedDateActive).startOf('day'),
              'day',
            );
          };

          return renderTable({
            index: index,
            inputProps: {
              name: 'sdToDate',
              value: value && dayjs(value).format('DD/MM/YYYY'),
            },
            component: (
              <DatePicker
                picker="date"
                disabledDate={disabledDate}
                placeholder="Chọn ngày kết thúc"
                format={{ format: 'DD/MM/YYYY' }}
              />
            ),
            rules: [{ required: true, message: 'Chọn ngày kết thúc' }],
          });
        },
      },
      {
        width: 90,
        title: 'Công ty',
        dataIndex: 'company',
        className: 'align-top',
        render: (value) => value || '-',
      },
      {
        width: 90,
        title: 'Kênh',
        dataIndex: 'channel',
        className: 'align-top',
        render: (value) => value || '-',
      },
      {
        width: 100,
        title: 'Cửa hàng',
        dataIndex: 'store',
        className: 'align-top',
        render: (value) => value || '-',
      },
      {
        width: 160,
        title: 'Người tạo',
        className: 'align-top',
        dataIndex: ['tcreateUser', 'fullName'],
        render: (value) => value || '-',
      },
      {
        width: 180,
        title: 'Ngày tạo',
        className: 'align-top',
        dataIndex: 'createDate',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        width: 160,
        title: 'Người cập nhật',
        className: 'align-top',
        dataIndex: ['tmodifyUser', 'fullName'],
        render: (value) => value || '-',
      },
      {
        width: 180,
        title: 'Ngày cập nhật',
        className: 'align-top',
        dataIndex: 'modifyDate',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      // {
      //   width: 190,
      //   title: '',
      //   fixed: 'right',
      //   align: 'center',
      //   className: 'align-top',
      //   render: (_, record: ISaleDeal, index) =>
      //     index === selectedIndex ? (
      //       <Space size={'middle'} className="text-lg">
      //         <ButtonAction tooltip="Lưu" onClick={() => handleSave(record)}>
      //           <SaveTwoTone style={{ fontSize: 20 }} />
      //         </ButtonAction>
      //         <ButtonAction tooltip="Hủy" onClick={handleCancel}>
      //           <CloseCircleTwoTone style={{ fontSize: 20 }} />
      //         </ButtonAction>
      //       </Space>
      //     ) : (
      //       <Space size={'middle'} className="text-lg ">
      //         <ButtonAction
      //           tooltip="Chỉnh sửa"
      //           disabled={isDisabled}
      //           onClick={() => handleClickEditRow(index)}
      //         >
      //           <EditIcon />
      //         </ButtonAction>
      //         <Switch
      //           disabled={isDisabled}
      //           value={record.status}
      //           onChange={() => handleChangeSwitch(record)}
      //         />
      //         <ButtonAction tooltip="Xoá" disabled={isDisabled}>
      //           <XIcon
      //             className="cursor-pointer"
      //             onClick={() => {
      //               if (selectedIndex !== -1) return;

      //               confirm({
      //                 okText: 'Xác nhận',
      //                 cancelText: 'Huỷ',
      //                 title: `Bạn có muốn xoá Chương trình KM ${record.voucherName} ?`,
      //                 onOk() {
      //                   handleClickDeleteRow(record.id);
      //                 },
      //               });
      //             }}
      //           />
      //         </ButtonAction>
      //       </Space>
      //     ),
      // },
    ];
  }, [data, selectedIndex, isDisabled]);

  useEffect(() => {
    if (!isAddRow) return;

    setData((prev) => ({
      ...prev,
      items: [newItem, ...prev.items],
    }));
    setIsAddRow(false);
    setSelectedIndex(0);
  }, [data]);

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          columns={columns}
          loading={loading}
          dataSource={data.items}
          scroll={{ x: 3000 }}
          pagination={{
            disabled: isDisabled,
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
});

export default memo(TableSaleDeal);
